/**
 * DataBest — PerfPeriodSelector
 * Preset (7d/30d/90d) + Custom range with DatePickerUS.
 * Emits ISO YYYY-MM-DD strings via onChange({period_start, period_end}).
 */
import React from "react";
import DatePickerUS from "@/components/DatePickerUS";
import { todayISO } from "@/lib/dates";

const addDays = (iso, days) => {
    const d = new Date(iso + "T00:00:00");
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};

export const buildDefaultPeriod = (days = 30) => ({
    period_start: addDays(todayISO(), -(days - 1)),
    period_end: todayISO(),
});

const PRESETS = [
    { label: "7d", days: 7 },
    { label: "30d", days: 30 },
    { label: "90d", days: 90 },
];

const PerfPeriodSelector = ({ value, onChange }) => {
    const { period_start, period_end } = value;
    const spanDays =
        (new Date(period_end) - new Date(period_start)) / 86400000 + 1;
    const matchingPreset = PRESETS.find((p) => p.days === spanDays);
    const isCustom = !matchingPreset;

    return (
        <div
            className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center"
            data-testid="perf-period-selector"
        >
            <div className="inline-flex rounded-lg bg-slate-100 p-1">
                {PRESETS.map((p) => (
                    <button
                        key={p.label}
                        type="button"
                        onClick={() => onChange(buildDefaultPeriod(p.days))}
                        data-testid={`perf-period-${p.label}`}
                        data-active={
                            (matchingPreset?.label === p.label) || undefined
                        }
                        className={`rounded-md px-3 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                            matchingPreset?.label === p.label
                                ? "bg-white text-brand-blue-700 shadow-sm"
                                : "text-slate-500 hover:text-slate-800"
                        }`}
                    >
                        {p.label}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={() =>
                        onChange({
                            period_start,
                            period_end,
                        })
                    }
                    data-active={isCustom || undefined}
                    data-testid="perf-period-custom"
                    className={`rounded-md px-3 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                        isCustom
                            ? "bg-white text-brand-blue-700 shadow-sm"
                            : "text-slate-500 hover:text-slate-800"
                    }`}
                >
                    Custom
                </button>
            </div>
            <div className="inline-flex items-center gap-1">
                <DatePickerUS
                    value={period_start}
                    onChange={(iso) =>
                        iso &&
                        onChange({ period_start: iso, period_end })
                    }
                    max={period_end}
                    testId="perf-period-start"
                />
                <span className="text-slate-400">→</span>
                <DatePickerUS
                    value={period_end}
                    onChange={(iso) =>
                        iso &&
                        onChange({ period_start, period_end: iso })
                    }
                    min={period_start}
                    max={todayISO()}
                    testId="perf-period-end"
                />
            </div>
        </div>
    );
};

export default PerfPeriodSelector;
